import * as React from "react";
import { connect } from "react-redux";

import { Alert } from "@mantine/core";
import { DateTime } from "luxon";
import { bindActionCreators } from "redux";
import { getAvailabilities } from "../../../data/actions/availabilityActions";
import { IState } from "../../../data/reducers/state";
import {
  IAppointment,
  IAppointmentDetail,
} from "../../../types/appointments/Appointment";
import { IAssociation } from "../../../types/association/Association";
import { IAvailability } from "../../../types/availabilities/Availabilities";
import { IPerson } from "../../../types/persons/person";
import AppointmentPersonSummary from "../../appointments/AppointmentPersonSummary";
import TextSeparator from "../../common/TextSeparator";
import AvailabilityHistory from "../../profile/AvailabilityHistory";
import AvailabilitySimulator from "../generic/AvailabilitySimulator";

interface IRosterPersonMoreInfoProps {
  person: IPerson;
  association: IAssociation;
  appointments: IAppointment<IAppointmentDetail>[];
  hasAvailabilities: boolean;

  relevantTime?: DateTime;
  shiftContext?: string;

  actions: {
    getAvailabilities: (person) => Promise<IAvailability[]>;
  };
}

const RosterPersonMoreInfo: React.FunctionComponent<
  IRosterPersonMoreInfoProps
> = ({ person, appointments, hasAvailabilities, association, actions, ...props }) => {
  const [availabilitiesError, setAvailabilitiesError] = React.useState<
    string | null
  >();
  const personId = person._id;
  React.useEffect(() => {
    if (hasAvailabilities) return;
    actions
      .getAvailabilities(personId)
      .catch((err) => setAvailabilitiesError(err.toString()));
  }, [personId]);

  return (
    <div padding="md">
      <h3><a href={`/u/${person.externalIdentifier}`} target="_blank">{person?.fullName}</a></h3>
      {person?.mobile && (<p>Phone: <a href={`tel:${person.mobile}`}>{person.mobile}</a></p>)}
      <TextSeparator>Appointments</TextSeparator>
      {(appointments?.length || 0) > 0 ? (
        <AppointmentPersonSummary
          person={personId}
          appointments={appointments}
        />
      ) : (
        <Alert color="blue">No appointments</Alert>
      )}
      <TextSeparator>Availability</TextSeparator>
      <AvailabilitySimulator personId={personId} shiftContext={props.shiftContext} defaultStart={props.relevantTime} />
      {!availabilitiesError ? (
        <AvailabilityHistory
          personId={personId || undefined}
          relevantTime={props.relevantTime}
          shiftContext={props.shiftContext}
          canEdit={false}
          isUnavailableByDefault={
            association?.config?.availabilities?.membersAreUnavailableByDefault ?? false
          }
        />
      ) : (
        <Alert color="red">
          Error loading availabilities: {availabilitiesError}
        </Alert>
      )}
    </div>
  );
};

function mapStateToProps(
  state: IState,
  props: IRosterPersonMoreInfoProps,
): Partial<IRosterPersonMoreInfoProps> {
  return {
    appointments: state.appointments.byPerson?.[props.person?._id]?.map(
      (apId) => state.appointments.map[apId],
    ),
    hasAvailabilities: props.person?._id in state.availabilities.byPerson,
  };
}

function mapDispatchToProps(dispatch): Partial<IRosterPersonMoreInfoProps> {
  return {
    actions: bindActionCreators({ getAvailabilities }, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RosterPersonMoreInfo);
